import type { ReactElement } from 'react';

interface StandLogoProps {
  url: string | null;
  /** Tailwind size class for the circle, e.g. `size-11`. */
  className: string;
  /** Exhibitor name, for the accessible label. Omit to mark the logo decorative. */
  alt?: string;
}

/**
 * An exhibitor logo in a fixed-size circle.
 *
 * Drawn as a background rather than an `<img>`: logos arrive at whatever aspect
 * ratio the exhibitor uploaded, and a plain `<img>` in a square box stretched
 * the tall ones. `contain` scales each to fit inside the circle with its
 * proportions intact, and centring keeps the padding even.
 *
 * The white disc gives logos designed for light backgrounds (dark text, no
 * transparency handling) contrast against the dark theme. The logo sits on an
 * inner layer inset 15% from the disc's edge, which scales with the circle at
 * every size and keeps even a square logo's corners inside it. An inset rather
 * than padding: percentage padding resolves against the parent's width, so on
 * a card it ballooned the circle and left no room for the logo.
 */
export function StandLogo({ url, className, alt }: StandLogoProps): ReactElement | null {
  if (url === null || url === '') {
    return null;
  }

  return (
    <span
      className={`relative shrink-0 rounded-full bg-white ${className}`}
      role={alt === undefined ? 'presentation' : 'img'}
      {...(alt !== undefined && { 'aria-label': alt })}
    >
      <span
        className="absolute inset-[15%] bg-contain bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${JSON.stringify(url)})` }}
      />
    </span>
  );
}
